/**
 * BattlesTab — lobby view for BESC Draw Battles
 *
 * Shows open battles (joinable) and active battles (watchable).
 * Lets users create new battles with tier / mode / token / slots choices.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReadContract, useWriteContract, usePublicClient } from "wagmi";
import { formatUnits } from "viem";
import { helperToast } from "lib/helperToast";
import { CARD_PACK_BATTLE_ABI, CARD_PACK_BATTLE_ADDRESS } from "../CardPackBattleABI";
import { CARD_PACK_SHOP_ABI }                             from "../CardPackShopABI";
import { TIER_DATA, TOKEN_SYMBOLS }                       from "./PackSelector";
import type { BattleData }                                from "./BattleArena";
import { DEMO_MODE, DEMO_BLOCKED_MSG }                    from "../demoMode";
import "./BattlesTab.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const SHOP_ADDRESS = (import.meta.env.VITE_CARD_PACK_SHOP_ADDRESS ??
  "0x0000000000000000000000000000000000000000") as `0x${string}`;

const TOKEN_ADDRESSES: Record<number, `0x${string}`> = {
  0: "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B",
  1: "0xFA4D586E7bF3E89909DB7ACC769af33731aC6355",
  2: "0x86fc715AF0231b9eeA8d6dfcFf5e91E776E270A4",
};

const ERC20_ABI = [
  { inputs:[{name:"owner",type:"address"},{name:"spender",type:"address"}], name:"allowance", outputs:[{name:"",type:"uint256"}], stateMutability:"view", type:"function" },
  { inputs:[{name:"spender",type:"address"},{name:"amount",type:"uint256"}], name:"approve",   outputs:[{name:"",type:"bool"}],    stateMutability:"nonpayable", type:"function" },
] as const;

const MODE_LABELS  = ["⚔️ Standard", "👾 Underdog"] as const;
const MODE_DESC    = ["Highest card value wins", "Lowest card value wins"] as const;
const SLOTS_LABELS = ["1v1", "3-Way"] as const;

const API_BASE = import.meta.env.VITE_INDEXER_API ?? "";
const WS_BASE  = API_BASE.replace(/^http/, "ws");

// ─── Types ────────────────────────────────────────────────────────────────────
interface LobbyBattle {
  battleId:    string;
  tier:        number;
  payToken:    number;
  mode:        number;
  maxSlots:    number;
  filledSlots: number;
  entryAmount: string;
  players:     string[];
  status:      "open" | "active" | "resolved" | "cancelled";
  createdAt:   number;
  isPrivate:   boolean;
}

interface BattlesTabProps {
  account:       string | undefined;
  onEnterBattle: (battle: BattleData) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Short tier name: "Mini Pack" → "Mini" */
function tierShortName(t: typeof TIER_DATA[0]): string {
  return t.name.replace(" Pack", "");
}

/** USD face value from BUSDC priceRaw (stablecoin = 1:1 USD, 18 dec) */
function tierUsd(t: typeof TIER_DATA[0]): number {
  try {
    const raw = (t as any).priceRaw?.[0] ?? "0";
    return Number(BigInt(raw.replace(",",""))) / 1e18;
  } catch { return 0; }
}

function fmtToken(val: bigint | string | undefined, token: number): string {
  if (val == null) return "…";
  const n = Number(formatUnits(BigInt(val.toString()), 18));
  const sym = TOKEN_SYMBOLS[token as 0|1|2|3|4] ?? "?";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${sym}`;
}

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  return `${Math.floor(s/3600)}h ago`;
}

function shortAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

// ─── BattleCard component ─────────────────────────────────────────────────────
function BattleCard({ battle, account, onJoin, onWatch, joiningId }: {
  battle:    LobbyBattle;
  account:   string | undefined;
  onJoin:    (b: LobbyBattle) => void;
  onWatch:   (b: LobbyBattle) => void;
  joiningId: string | null;
}) {
  const tier        = TIER_DATA[battle.tier];
  const isOpen      = battle.status === "open";
  const tierUsdVal  = tier ? tierUsd(tier) : 0;
  const prizeUsd    = tierUsdVal > 0
    ? `$${(tierUsdVal * battle.maxSlots * 0.95).toFixed(2)}`
    : "…";

  const playerInBattle = account &&
    battle.players.some(p => p.toLowerCase() === account.toLowerCase());

  return (
    <motion.div
      className={`BT__card BT__card--${battle.status}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      layout
    >
      {/* Header badges */}
      <div className="BT__card-badges">
        <span className="BT__badge BT__badge--tier">{tier ? tierShortName(tier) : `Tier ${battle.tier}`}</span>
        <span className="BT__badge BT__badge--mode">{MODE_LABELS[battle.mode as 0|1] ?? "?"}</span>
        <span className="BT__badge BT__badge--token">{TOKEN_SYMBOLS[battle.payToken as 0|1|2|3|4] ?? "?"}</span>
        {battle.maxSlots === 3 && <span className="BT__badge BT__badge--slots">3-Way</span>}
      </div>

      {/* Prize pool */}
      <div className="BT__card-prize">
        <span className="BT__prize-label">Prize Pool</span>
        <span className="BT__prize-amount">{prizeUsd}</span>
        <span className="BT__prize-sub">after 5% rake</span>
      </div>

      {/* Players bubbles */}
      <div className="BT__card-players">
        {Array.from({ length: battle.maxSlots }).map((_, i) => {
          const addr = battle.players[i];
          const isMe = addr && account && addr.toLowerCase() === account.toLowerCase();
          return (
            <div key={i} className={`BT__player-bubble${addr ? " filled" : ""}${isMe ? " me" : ""}`}>
              {addr ? (isMe ? "YOU" : shortAddr(addr)) : <span className="BT__empty-slot">?</span>}
            </div>
          );
        })}
        <span className="BT__slots-count">{battle.filledSlots}/{battle.maxSlots}</span>
      </div>

      {/* Status line */}
      <div className="BT__card-meta">
        <span className={`BT__status-dot BT__status-dot--${battle.status}`} />
        <span className="BT__status-label">
          {isOpen ? "Waiting for players" : battle.status === "active" ? "⚔️ In progress" : battle.status}
        </span>
        <span className="BT__time-ago">{timeAgo(battle.createdAt)}</span>
      </div>

      {/* CTA */}
      <div className="BT__card-actions">
        {isOpen && !playerInBattle && (
          <button
            className="BT__btn BT__btn--join"
            onClick={() => onJoin(battle)}
            disabled={joiningId !== null}>
            {joiningId === battle.battleId
              ? "Joining…"
              : `Join — ${fmtToken(battle.entryAmount, battle.payToken)}`}
          </button>
        )}
        {isOpen && playerInBattle && (
          <button className="BT__btn BT__btn--waiting" disabled>
            Waiting for opponent…
          </button>
        )}
        {battle.status === "active" && (
          <button className="BT__btn BT__btn--watch" onClick={() => onWatch(battle)}>
            {playerInBattle ? "⚔️ Enter Arena" : "👁 Spectate"}
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ─── CreateModal ──────────────────────────────────────────────────────────────
function CreateModal({ account, onClose, onCreate }: {
  account:  string | undefined;
  onClose:  () => void;
  onCreate: (battleId: string, battle: BattleData) => void;
}) {
  const [tier,      setTier]      = useState(1);   // STARTER $10
  const [payToken,  setPayToken]  = useState(0);   // BUSDC
  const [mode,      setMode]      = useState(0);   // Standard
  const [maxSlots,  setMaxSlots]  = useState(2);   // 1v1
  const [isPrivate, setIsPrivate] = useState(false);
  const [busy,      setBusy]      = useState(false);

  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  // Entry fee from shop
  const { data: entryRaw } = useReadContract({
    address: SHOP_ADDRESS,
    abi:     CARD_PACK_SHOP_ABI,
    functionName: "packPrice",
    args:    [tier, payToken],
  });

  const entryFee     = entryRaw as bigint | undefined;
  const tierData     = TIER_DATA[tier];
  const tierUsdVal   = tierUsd(tierData);
  // Prize = entryFee (per player) × players × 0.95 — shown in USD for BUSDC entry,
  // or estimated USD for other tokens (oracle-driven, so we use tier face value as est.)
  const prizeUsdEst  = tierUsdVal * maxSlots * 0.95;
  const needsApproval = payToken !== 3; // non-native needs ERC20 approval

  // Check allowance
  const tokenAddr = TOKEN_ADDRESSES[payToken] as `0x${string}` | undefined;
  const { data: allowanceRaw } = useReadContract({
    address:      tokenAddr,
    abi:          ERC20_ABI,
    functionName: "allowance",
    args:         [account as `0x${string}`, CARD_PACK_BATTLE_ADDRESS],
    query:        { enabled: !!account && !!tokenAddr && payToken !== 3 && payToken !== 4 },
  });
  const allowance = (allowanceRaw as bigint | undefined) ?? 0n;
  const needsErc20Approval = needsApproval && payToken !== 4 && entryFee != null && allowance < entryFee;

  const handleCreate = useCallback(async () => {
    if (DEMO_MODE) { helperToast.info(DEMO_BLOCKED_MSG); return; }
    if (!account) { helperToast.error("Connect wallet first"); return; }
    setBusy(true);
    try {
      // ERC20 approve if needed
      if (needsErc20Approval && tokenAddr && entryFee != null) {
        await writeContractAsync({
          address: tokenAddr,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [CARD_PACK_BATTLE_ADDRESS, entryFee * 10n],
        });
        helperToast.success("Token approved ✓");
      }

      // Create battle
      const txHash = await writeContractAsync({
        address:      CARD_PACK_BATTLE_ADDRESS,
        abi:          CARD_PACK_BATTLE_ABI,
        functionName: "createBattle",
        args:         [tier, payToken, mode, maxSlots, isPrivate],
        value:        payToken === 3 ? entryFee ?? 0n : 0n,
      });

      helperToast.success("Battle created! Waiting for opponent…");

      // Wait for receipt to get battleId from event
      if (publicClient && txHash) {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash as `0x${string}` });
        // Extract battleId from BattleCreated event
        const battleId = receipt.logs[0]?.topics[1]
          ? BigInt(receipt.logs[0].topics[1]).toString()
          : "1";

        const battleData: BattleData = {
          battleId,
          tier,
          payToken,
          mode,
          maxSlots,
          entryAmount: entryFee?.toString() ?? "0",
          mySlot:      0,
          myAddress:   account.toLowerCase(),
          players:     [account.toLowerCase()],
          seeds:       [],
          status:      "open",
        };

        onCreate(battleId, battleData);
      }
    } catch (err: any) {
      helperToast.error(err?.shortMessage ?? err?.message ?? "Transaction failed");
    } finally {
      setBusy(false);
    }
  }, [account, tier, payToken, mode, maxSlots, isPrivate, entryFee, needsErc20Approval, tokenAddr, writeContractAsync, publicClient, onCreate]);

  return (
    <motion.div className="BT__modal-overlay" onClick={onClose}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="BT__modal" onClick={e => e.stopPropagation()}
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}>

        <div className="BT__modal-header">
          <h2>⚔️ Create Battle</h2>
          <button className="BT__modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Tier selector */}
        <label className="BT__label">Pack Tier</label>
        <div className="BT__tier-grid">
          {TIER_DATA.map((t, i) => (
            <button key={i}
              className={`BT__tier-btn${tier === i ? " active" : ""}`}
              onClick={() => setTier(i)}>
              <span className="BT__tier-name">{tierShortName(t)}</span>
              <span className="BT__tier-usd">${tierUsd(t)}</span>
            </button>
          ))}
        </div>

        {/* Mode */}
        <label className="BT__label">Battle Mode</label>
        <div className="BT__toggle-row">
          {([0, 1] as const).map(m => (
            <button key={m}
              className={`BT__toggle-btn${mode === m ? " active" : ""}`}
              onClick={() => setMode(m)}>
              <span>{MODE_LABELS[m]}</span>
              <span className="BT__toggle-desc">{MODE_DESC[m]}</span>
            </button>
          ))}
        </div>

        {/* Payment token */}
        <label className="BT__label">Pay With</label>
        <div className="BT__token-row">
          {([0,1,2,3,4] as const).map(t => (
            <button key={t}
              className={`BT__token-btn${payToken === t ? " active" : ""}`}
              onClick={() => setPayToken(t)}>
              {TOKEN_SYMBOLS[t]}
            </button>
          ))}
        </div>

        {/* Slots */}
        <label className="BT__label">Format</label>
        <div className="BT__toggle-row">
          {([2, 3] as const).map((s, i) => (
            <button key={s}
              className={`BT__toggle-btn${maxSlots === s ? " active" : ""}`}
              onClick={() => setMaxSlots(s)}>
              <span>{SLOTS_LABELS[i]}</span>
              <span className="BT__toggle-desc">{s} players</span>
            </button>
          ))}
        </div>

        {/* Private toggle */}
        <div className="BT__private-row">
          <label className="BT__label" style={{ margin: 0 }}>Private (share link only)</label>
          <button
            className={`BT__private-toggle${isPrivate ? " active" : ""}`}
            onClick={() => setIsPrivate(!isPrivate)}>
            {isPrivate ? "🔒 Private" : "🌐 Public"}
          </button>
        </div>

        {/* Summary */}
        <div className="BT__summary">
          <div className="BT__summary-row">
            <span>Entry fee</span>
            <span>{entryFee != null ? fmtToken(entryFee, payToken) : "…"}</span>
          </div>
          <div className="BT__summary-row">
            <span>Prize pool ({maxSlots}× entry, 95%)</span>
            <span className="BT__summary-prize">≈${prizeUsdEst.toFixed(2)}</span>
          </div>
          <div className="BT__summary-row dimmed">
            <span>Platform rake</span>
            <span>5%</span>
          </div>
        </div>

        <button
          className="BT__btn BT__btn--create"
          onClick={handleCreate}
          disabled={busy || !account}>
          {busy ? "Creating…" : needsErc20Approval ? "Approve + Create" : "Create Battle"}
        </button>
      </motion.div>
    </motion.div>
  );
}

// ─── Main BattlesTab ──────────────────────────────────────────────────────────
export function BattlesTab({ account, onEnterBattle }: BattlesTabProps) {
  const [battles,       setBattles]       = useState<LobbyBattle[]>([]);
  const [lobbyTab,      setLobbyTab]      = useState<"open" | "active">("open");
  const [showCreate,    setShowCreate]    = useState(false);
  const [joiningId,     setJoiningId]     = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  // ── Fetch initial battle list from backend ────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/api/battles`)
      .then(r => r.json())
      .then((list: LobbyBattle[]) => setBattles(list))
      .catch(() => {});
  }, []);

  // ── WebSocket real-time updates ───────────────────────────────────────────
  useEffect(() => {
    if (!WS_BASE) return;
    const ws = new WebSocket(`${WS_BASE}/ws/battles`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        switch (msg.type) {
          case "battle_created":
            setBattles(prev => [{
              battleId:    msg.data.battleId,
              tier:        msg.data.tier,
              payToken:    msg.data.payToken,
              mode:        msg.data.mode,
              maxSlots:    msg.data.maxSlots,
              filledSlots: 1,
              entryAmount: msg.data.entryAmount,
              players:     [msg.data.creator],
              status:      "open",
              createdAt:   Date.now(),
              isPrivate:   msg.data.isPrivate,
            }, ...prev]);
            break;
          case "battle_slot_filled":
            setBattles(prev => prev.map(b =>
              b.battleId === msg.data.battleId
                ? { ...b, filledSlots: b.filledSlots + 1, players: [...b.players, msg.data.player] }
                : b
            ));
            break;
          case "battle_active":
            setBattles(prev => prev.map(b =>
              b.battleId === msg.data.battleId ? { ...b, status: "active" } : b
            ));
            break;
          case "battle_resolved":
          case "battle_cancelled":
            setBattles(prev => prev.filter(b => b.battleId !== msg.data.battleId));
            break;
        }
      } catch {}
    };

    ws.onerror = () => {};
    return () => { ws.close(); };
  }, []);

  // ── Join a battle ─────────────────────────────────────────────────────────
  const handleJoin = useCallback(async (battle: LobbyBattle) => {
    if (DEMO_MODE) { helperToast.info(DEMO_BLOCKED_MSG); return; }
    if (!account) { helperToast.error("Connect wallet first"); return; }
    setJoiningId(battle.battleId);
    try {
      const txHash = await writeContractAsync({
        address:      CARD_PACK_BATTLE_ADDRESS,
        abi:          CARD_PACK_BATTLE_ABI,
        functionName: "joinBattle",
        args:         [BigInt(battle.battleId)],
        value:        battle.payToken === 3 ? BigInt(battle.entryAmount) : 0n,
      });

      helperToast.success("Joined battle! Entering arena…");

      if (publicClient && txHash) {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash as `0x${string}` });
        // Get my slot index from SlotFilled event
        const slotIdx = 1; // as joiner, always slot ≥1

        // Extract seeds from SlotFilled events in receipt
        const seeds: string[] = [];
        receipt.logs.forEach(log => {
          if (log.topics[0] && log.data) {
            // BattleSlotFilled: topics[1]=battleId, topics[2]=player, data=slotIdx+seed
            seeds.push("0"); // placeholder; arena fetches real data from contract
          }
        });

        const battleData: BattleData = {
          battleId:    battle.battleId,
          tier:        battle.tier,
          payToken:    battle.payToken,
          mode:        battle.mode,
          maxSlots:    battle.maxSlots,
          entryAmount: battle.entryAmount,
          mySlot:      slotIdx,
          myAddress:   account.toLowerCase(),
          players:     [...battle.players, account.toLowerCase()],
          seeds,
          status:      "active",
        };
        onEnterBattle(battleData);
      }
    } catch (err: any) {
      helperToast.error(err?.shortMessage ?? err?.message ?? "Transaction failed");
    } finally {
      setJoiningId(null);
    }
  }, [account, writeContractAsync, publicClient, onEnterBattle]);

  // ── Watch/spectate ────────────────────────────────────────────────────────
  const handleWatch = useCallback((battle: LobbyBattle) => {
    const mySlot = battle.players.findIndex(
      p => account && p.toLowerCase() === account.toLowerCase()
    );
    const battleData: BattleData = {
      battleId:    battle.battleId,
      tier:        battle.tier,
      payToken:    battle.payToken,
      mode:        battle.mode,
      maxSlots:    battle.maxSlots,
      entryAmount: battle.entryAmount,
      mySlot:      mySlot >= 0 ? mySlot : -1,
      myAddress:   account?.toLowerCase() ?? "",
      players:     battle.players,
      seeds:       [],
      status:      "active",
    };
    onEnterBattle(battleData);
  }, [account, onEnterBattle]);

  // ── Handle battle creation ────────────────────────────────────────────────
  const handleCreated = useCallback((_battleId: string, battleData: BattleData) => {
    setShowCreate(false);
    onEnterBattle(battleData);
  }, [onEnterBattle]);

  const openBattles   = battles.filter(b => b.status === "open");
  const activeBattles = battles.filter(b => b.status === "active");
  const shown         = lobbyTab === "open" ? openBattles : activeBattles;

  return (
    <div className="BT">
      {/* Header */}
      <div className="BT__header">
        <div className="BT__header-left">
          <h2 className="BT__title">⚔️ Battles</h2>
          <p className="BT__subtitle">
            Open a pack head-to-head — highest card value wins the pot
          </p>
        </div>
        <button className="BT__btn BT__btn--new" onClick={() => setShowCreate(true)}>
          + Create Battle
        </button>
      </div>

      {/* Mode tabs */}
      <div className="BT__lobby-tabs">
        <button
          className={`BT__lobby-tab${lobbyTab === "open" ? " active" : ""}`}
          onClick={() => setLobbyTab("open")}>
          Open {openBattles.length > 0 && <span className="BT__tab-count">{openBattles.length}</span>}
        </button>
        <button
          className={`BT__lobby-tab${lobbyTab === "active" ? " active" : ""}`}
          onClick={() => setLobbyTab("active")}>
          ⚔️ Live {activeBattles.length > 0 && <span className="BT__tab-count">{activeBattles.length}</span>}
        </button>
      </div>

      {/* Battle grid */}
      <div className="BT__grid">
        <AnimatePresence mode="popLayout">
          {shown.length === 0 ? (
            <motion.div className="BT__empty"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {lobbyTab === "open"
                ? <>No open battles — <button className="BT__link" onClick={() => setShowCreate(true)}>create the first one</button>!</>
                : "No live battles right now. Check back soon!"}
            </motion.div>
          ) : (
            shown.map(b => (
              <BattleCard
                key={b.battleId}
                battle={b}
                account={account}
                onJoin={handleJoin}
                onWatch={handleWatch}
                joiningId={joiningId}
              />
            ))
          )}
        </AnimatePresence>
      </div>

      {/* How it works callout */}
      <div className="BT__info-strip">
        <div className="BT__info-item">
          <span className="BT__info-icon">💰</span>
          <span>Same entry fee as buying a pack solo</span>
        </div>
        <div className="BT__info-item">
          <span className="BT__info-icon">🃏</span>
          <span>Each player pulls 1 card from the same tier</span>
        </div>
        <div className="BT__info-item">
          <span className="BT__info-icon">🏆</span>
          <span>Highest card value wins the pot (Standard) or lowest (Underdog)</span>
        </div>
        <div className="BT__info-item">
          <span className="BT__info-icon">⚡</span>
          <span>Instant on-chain payout — 5% platform rake</span>
        </div>
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {showCreate && (
          <CreateModal
            account={account}
            onClose={() => setShowCreate(false)}
            onCreate={handleCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
